import styled, { keyframes } from "styled-components";

// Smooth entrance animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

// Shine effect for primary button
const shine = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

export const ConsentContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 460px;
  background: white;
  color: #18216d;
  z-index: 9999;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  animation: ${fadeIn} 0.7s ease-out forwards;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, 
      var(--primary-color, #f9c0f5) 0%, 
      var(--primary-light, #ffd5fa) 50%, 
      var(--primary-color, #f9c0f5) 100%);
    background-size: 200% 200%;
    animation: ${shine} 3s linear infinite;
  }
  
  @media screen and (max-width: 768px) {
    bottom: 20px;
    width: 92%;
  }
`;

export const ConsentContent = styled.div`
  padding: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const ConsentTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0 0 12px 0;
  color: #18216d;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '🍪';
    margin-right: 10px;
    font-size: 1.4rem;
  }
`;

export const ConsentText = styled.p`
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.6;
  color: #555;
  max-width: 100%;
  margin-bottom: 5px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 22px;
  width: 100%;
  justify-content: center;
  
  @media screen and (max-width: 480px) {
    flex-direction: column;
  }
`;

export const ConsentButton = styled.button<{ primary?: boolean }>`
  padding: 12px 24px;
  border: ${props => props.primary ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Apply beautiful gradient or solid color based on primary status */
  background: ${props => props.primary ? 
    `linear-gradient(45deg, var(--primary-color, #f9c0f5), var(--primary-light, #ffd5fa))` : 
    'white'};
  color: ${props => props.primary ? '#fff' : '#666'};
  
  /* Add subtle shadow for depth */
  box-shadow: ${props => props.primary ? 
    '0 4px 10px rgba(249, 192, 245, 0.3)' : 
    '0 2px 5px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? 
      '0 6px 15px rgba(249, 192, 245, 0.4)' : 
      '0 4px 10px rgba(0, 0, 0, 0.1)'};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${props => props.primary ? 
      '0 2px 5px rgba(249, 192, 245, 0.2)' : 
      '0 1px 3px rgba(0, 0, 0, 0.05)'};
  }
  
  /* Add micro-interaction for primary button */
  ${props => props.primary && `
    &:hover {
      background: linear-gradient(45deg, var(--primary-dark, #e8b3e4), var(--primary-color, #f9c0f5));
    }
  `}
`;

// These are no longer needed but keeping for reference in case we need to revert
export const CustomizeButton = styled.button`
  display: none;
`;

export const SettingsPanel = styled.div`
  display: none;
`;

export const SettingsOption = styled.div`
  display: none;
`;

export const CheckboxLabel = styled.label`
  display: none;
`;

export const Checkbox = styled.input`
  display: none;
`;